// SYNCED FROM: VidaReactNative/src/apis/lobApi.ts — keep in sync. See SYNC.md.
import axios from 'axios';

import { LOB_API_BASE_URL, getLobPublishableKey } from '../constants/lob';

export type LobVerifyRequest = {
  primary_line: string;
  secondary_line?: string;
  city?: string;
  state?: string;
  zip_code?: string;
};

export type LobDeliverability =
  | 'deliverable'
  | 'deliverable_unnecessary_unit'
  | 'deliverable_incorrect_unit'
  | 'deliverable_missing_unit'
  | 'undeliverable';

export type LobVerifyResponse = {
  primary_line: string;
  secondary_line: string;
  last_line: string;
  deliverability: LobDeliverability;
  components: {
    primary_number?: string;
    street_predirection?: string;
    street_name?: string;
    street_suffix?: string;
    street_postdirection?: string;
    secondary_designator?: string;
    secondary_number?: string;
    pmb_designator?: string;
    pmb_number?: string;
    extra_secondary_designator?: string;
    extra_secondary_number?: string;
    city: string;
    state: string;
    zip_code: string;
    zip_code_plus_4: string;
  };
  deliverability_analysis?: {
    dpv_footnotes?: string[];
  };
};

// Dedicated instance: Lob is an external service authed with the publishable
// key, NOT the Vida bearer — so it must not go through the shared apiClient.
//
// `auth` is deliberately NOT set here. This instance is built when the module is first
// imported, which is before `OnboardingRoot` publishes runtime config, so baking the key
// in would freeze it to the env fallback — empty in a consumer's bundle. Resolved per
// request below instead, the same reason `api/axiosConfig.ts` resolves `baseURL` in an
// interceptor.
const lobAxios = axios.create({
  baseURL: LOB_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 5000,
});

export const verifyUsAddress = async (req: LobVerifyRequest): Promise<LobVerifyResponse> => {
  const publishableKey = getLobPublishableKey();
  // Fail fast with a clear message if the publishable key is missing, rather than
  // firing a request with empty Basic-auth and surfacing a fuzzy 401 in the dialog.
  if (!publishableKey) {
    throw new Error('lobPublishableKey is not configured');
  }
  const response = await lobAxios.post<LobVerifyResponse>('/us_verifications', req, {
    params: { case: 'proper' },
    auth: { username: publishableKey, password: '' },
  });
  return response.data;
};
